import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { SERVICE_ICONS } from "@/lib/constants";
import { NO_CATEGORY, NO_CATEGORY_LABEL } from "@/lib/categories";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ServiceIcon } from "@/components/ServiceIcon";

const L = ({ children, htmlFor }) => <Label htmlFor={htmlFor} className="text-xs font-semibold text-slate-600">{children}</Label>;

export const CategoryDialog = ({ category, onClose, onSaved }) => {
  const isNew = category && !category.id;
  const [v, setV] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (category) setV({ name: category.name || "", description: category.description || "", icon: category.icon || "Stethoscope", active: category.active ?? true });
    setError("");
  }, [category]);
  const set = (k) => (val) => setV((s) => ({ ...s, [k]: val }));
  const save = async () => {
    setBusy(true);
    try {
      if (isNew) await api.post("/admin/service-categories", v); else await api.patch(`/admin/service-categories/${category.id}`, v);
      toast.success("Categoría guardada"); onSaved(); onClose();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!category} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl sm:max-w-md" data-testid="category-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl font-extrabold">{isNew ? "Nueva categoría" : "Editar categoría"}</DialogTitle></DialogHeader>
        <div className="grid gap-4">
          <div><L htmlFor="cat-name">Nombre</L><Input id="cat-name" maxLength={60} value={v.name || ""} onChange={(e) => set("name")(e.target.value)} className="mt-1.5 rounded-xl" data-testid="category-input-name" /></div>
          <div><L htmlFor="cat-desc">Descripción corta (opcional)</L><Textarea id="cat-desc" rows={2} maxLength={200} value={v.description || ""} onChange={(e) => set("description")(e.target.value)} className="mt-1.5 rounded-xl" data-testid="category-input-description" /></div>
          <div>
            <L>Ícono</L>
            <Select value={v.icon} onValueChange={set("icon")}>
              <SelectTrigger className="mt-1.5 rounded-xl" data-testid="category-icon-select"><SelectValue /></SelectTrigger>
              <SelectContent>{SERVICE_ICONS.map((i) => <SelectItem key={i} value={i}><span className="flex items-center gap-2"><ServiceIcon name={i} className="h-4 w-4" /> {i}</span></SelectItem>)}</SelectContent>
            </Select>
          </div>
          <label className="flex items-center justify-between rounded-xl bg-[#F5F7FA] p-3 text-sm font-medium">
            Activa (visible en el sitio) <Switch checked={v.active} onCheckedChange={set("active")} data-testid="category-active-switch" />
          </label>
          {error && <p className="text-sm text-rose-600" data-testid="category-dialog-error">{error}</p>}
          <Button onClick={save} disabled={!v.name?.trim() || busy} className="rounded-xl bg-[#1E6FD9]" data-testid="category-save-button">
            {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Guardar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const DeleteCategoryDialog = ({ category, categories, onClose, onDone }) => {
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setTarget(""); }, [category]);
  if (!category) return null;
  const n = category.service_count || 0;
  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/service-categories/${category.id}`, { params: n ? { move_to: target } : {} });
      toast.success(n ? `Categoría eliminada · ${n} servicio(s) movido(s)` : "Categoría eliminada"); onDone(); onClose();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl sm:max-w-md" data-testid="category-delete-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-extrabold">Eliminar «{category.name}»</DialogTitle>
          <DialogDescription>{n ? `Esta categoría tiene ${n} servicio(s). Elige a dónde moverlos antes de eliminarla.` : "La categoría no tiene servicios."}</DialogDescription>
        </DialogHeader>
        {n > 0 && (
          <div>
            <L>Mover servicios a</L>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger className="mt-1.5 rounded-xl" data-testid="category-move-select"><SelectValue placeholder="Elige una categoría" /></SelectTrigger>
              <SelectContent>
                {categories.filter((c) => c.id !== category.id).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                <SelectItem value={NO_CATEGORY}>{NO_CATEGORY_LABEL}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-xl" data-testid="category-delete-cancel">Cancelar</Button>
          <Button onClick={remove} disabled={busy || (n > 0 && !target)} className="rounded-xl bg-rose-600 hover:bg-rose-700" data-testid="category-delete-confirm">
            {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} {n ? "Mover y eliminar" : "Eliminar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
